import { useState, useEffect, useRef, ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { CloudWatchLogEvent } from '@localstack/types';
import { Typography, Box, Card, Alert } from '@mui/material';
import { useRoutes, useAwsEffect } from '@localstack/services';
import { Breadcrumbs, LogsTable, LogsTableToolbar, LogsTableEvent } from '@localstack/ui';
import { DEFAULT_CLOUDWATCH_ROUTES } from '@localstack/constants';

import { LogGroupNavTabs } from './components';
import { CloudWatchProps } from './types';

// inefficient
const sortEvents = (events: LogsTableEvent[]): LogsTableEvent[] =>
  [...events].sort((le, re) => new Date(le.timestamp).getTime() - new Date(re.timestamp).getTime());

// inefficient
const filterEvents = (eventsIndex: { [key: string]: boolean }, events: CloudWatchLogEvent[]) =>
  events.filter(({ eventId }) => !eventsIndex[eventId as string]);

const transformEvents = (events: CloudWatchLogEvent[]): LogsTableEvent[] =>
  events.map((event) => ({
    id: event.eventId as string,
    groupName: event.logStreamName,
    timestamp: event.ingestionTime as number,
    message: event.message as string,
  }));

const DAY_MS = 24 * 60 * 60 * 1000; // 24 hours

export const CloudWatchLogGroupEvents = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_CLOUDWATCH_ROUTES,
}: CloudWatchProps): ReactElement => {
  const { goto } = useRoutes();
  const { logGroupName, logStreamName } = useParams<'logGroupName' | 'logStreamName'>();

  const [liveMode, setLiveMode] = useState(true);
  const [pattern, setPattern] = useState<string | undefined>(undefined);
  const [events, setEvents] = useState<LogsTableEvent[]>([]);
  const [eventsIndex, setEventsIndex] = useState<{ [key: string]: boolean }>({});

  // Live mode polls every second, so a failure that persists (typically a log group that does
  // not exist yet) would raise a snackbar every second. Show it inline instead.
  const { filterLogEvents, error } = useAwsEffect('CloudWatchLogs', ['filterLogEvents'], {
    revalidate: ['filterLogEvents'],
    clientOverrides,
    silentErrors: true,
  });

  // `error` from useAwsEffect is sticky — it is set on failure and never cleared on success — so
  // track the last call's outcome to know whether it still describes the current state.
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchEvents = async (startTime: number, endTime: number) => {
    const loadedEvents = await filterLogEvents({
      logGroupName: logGroupName as string,
      filterPattern: pattern,
      startTime,
      endTime,
      logStreamNames: logStreamName ? [logStreamName] : undefined,
    });

    // useAwsEffect swallows the rejection and resolves to undefined on failure
    setLoadFailed(!loadedEvents);

    return loadedEvents;
  };

  // Returns whether the call succeeded, so the live poll below can back off on failure.
  const loadForward = async (): Promise<boolean> => {
    // next portion of events starts from the latest available timestamp + 1 day
    const latestTimestamp = events.length
      ? new Date(events[events.length - 1]?.timestamp as Date | number)
      : new Date(new Date().getTime() - DAY_MS);

    const loadedEvents = await fetchEvents(latestTimestamp.getTime(), new Date().getTime());

    if (!loadedEvents) return false;

    const newEvents = filterEvents(eventsIndex, loadedEvents?.events ?? []);

    const transformedNewEvents = transformEvents(newEvents);

    const allEvents = sortEvents([...events, ...transformedNewEvents]);

    setEventsIndex((prevIndex) => {
      const nextIndex = { ...prevIndex };
      transformedNewEvents.forEach((event) => {
        nextIndex[event.id] = true;
      });
      return nextIndex;
    });

    setEvents(allEvents);

    return true;
  };

  const loadBackward = async () => {
    if (!events.length) return;

    // previous portion of events ends with the earliest available timestamp
    const earliestTimestamp = new Date(events[0]?.timestamp as Date | number);
    const startDate = new Date(earliestTimestamp.getTime() - DAY_MS);

    const loadedEvents = await fetchEvents(startDate.getTime(), earliestTimestamp.getTime());

    if (!loadedEvents) return;

    const newEvents = filterEvents(eventsIndex, loadedEvents?.events ?? []);

    const transformedNewEvents = transformEvents(newEvents);

    const allEvents = sortEvents([...events, ...transformedNewEvents]);

    setEventsIndex((prevIndex) => {
      const nextIndex = { ...prevIndex };
      transformedNewEvents.forEach((event) => {
        nextIndex[event.id] = true;
      });
      return nextIndex;
    });

    setEvents(allEvents);
  };

  // `loadForward` gets a new identity every render, so keep it out of the deps below via a ref —
  // as a dependency it tore down and rebuilt the poll on every render.
  const loadForwardRef = useRef(loadForward);
  loadForwardRef.current = loadForward;

  useEffect(() => {
    if (!liveMode) return;

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;
    let failures = 0;

    const poll = async () => {
      const succeeded = await loadForwardRef.current();
      if (cancelled) return;
      // Back off while calls keep failing, so a log group that does not exist yet costs a
      // couple of requests per minute instead of one per second forever. The first success
      // resets it, so the page still catches up on its own once the group appears.
      failures = succeeded ? 0 : failures + 1;
      timeout = setTimeout(poll, Math.min(1000 * 2 ** failures, 30_000));
    };

    timeout = setTimeout(poll, 1000);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [liveMode]);

  useEffect(() => {
    setEvents([]);
    setEventsIndex({});
    setPattern(undefined);
  }, [logStreamName]);

  return (
    <Layout
      documentTitle="CloudWatch Log Events"
      tabs={!logStreamName && <LogGroupNavTabs logGroupName={logGroupName as string} routes={routes} />}
      title={
        <Box>
          <Typography variant="h4">CloudWatch Log Events</Typography>
          <Breadcrumbs
            mappings={[
              ['CloudWatch Logs', () => goto(routes.RESOURCES_CLOUDWATCH)],
              ['Log Groups', () => goto(routes.RESOURCES_CLOUDWATCH_GROUPS)],
              [logGroupName, () => goto(routes.RESOURCES_CLOUDWATCH_GROUPS)],
              [
                logStreamName ? 'Log Streams' : null,
                () => goto(routes.RESOURCES_CLOUDWATCH_GROUP_STREAMS, { logGroupName }),
              ],
              [logStreamName, () => goto(routes.RESOURCES_CLOUDWATCH_GROUP_STREAMS, { logGroupName })],
              ['Log Events', null],
            ]}
          />
        </Box>
      }
    >
      <Card style={{ flex: 1, height: '100%' }}>
        <Box height="100%" display="flex" flexDirection="column">
          <LogsTableToolbar
            liveUpdate={liveMode}
            onToggleLiveMode={() => setLiveMode(!liveMode)}
            onApply={(filterPattern) => {
              setEvents([]);
              setEventsIndex({});
              setPattern(filterPattern);
            }}
          />
          {loadFailed && error && (
            <Box mt={2}>
              <Alert severity="warning">{error.message}</Alert>
            </Box>
          )}
          <Box flexGrow={1} mt={2}>
            <LogsTable
              events={events}
              onLoadForward={loadForward}
              onLoadBackward={loadBackward}
              onViewGroup={(streamName) =>
                goto(routes.RESOURCES_CLOUDWATCH_GROUP_STREAM_EVENTS, { logGroupName, logStreamName: streamName })
              }
            />
          </Box>
        </Box>
      </Card>
    </Layout>
  );
};
